import { IconButton } from '@creditornot/cb-button';
import { MoreHorizontal } from '@creditornot/cb-icons';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { LegacyPopover } from '@creditornot/cb-popover';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React, { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ConfirmModal } from 'common/components';
import { localize } from 'common/utils';
import { deleteOption } from 'modules/shared-menu/api';
import { getErrorResponseText } from 'modules/shared-menu/utils';

import { useSelectionData } from '../../../../hooks/useSelectionData';
import { Offer } from '../../types';

type Props = {
  onCopy?: (optionId: string) => void;
  optionId: string;
  primaryLanguage: Language;
};

const Root = styled.div``;

const StyledPopover = styled(LegacyPopover).attrs({
  placement: 'bottom-start',
  renderArrow: false,
})`
  display: flex;
  align-items: center;
`;

const ToggleButton = styled(IconButton).attrs({ variant: 'brand', size: 'tiny' })``;

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 200px;
  padding: 0 8px;
`;

export const getOptionDeletionErrorMessage = ({
  error,
  offers,
  primaryLanguage,
}: {
  error: any;
  offers: Offer[];
  primaryLanguage: Language;
}): string => {
  const errorMessage = getErrorResponseText(error);
  const regex =
    /^Offers \[\[([0-9a-fA-F]{24}(?:,\s*[0-9a-fA-F]{24})*)\]\] depend on the option value\(s\) being removed$/;
  if (!regex.test(errorMessage)) {
    return errorMessage;
  }
  const idRegex = /[0-9a-fA-F]{24}/g;
  const offerIds = errorMessage.match(idRegex);
  const dependentOffersName = offerIds
    ?.map(id => offers.find(offer => offer.id === id)?.name)
    .map(name => `\n- ${localize(name ?? [], primaryLanguage)}`)
    .join('');
  return `Some offers are using this option.\n\nPlease remove the option first from the following offers:"${dependentOffersName}"`;
};

export const OptionActions = ({ onCopy, optionId, primaryLanguage }: Props) => {
  const { selection, updateSelectionData, offers } = useSelectionData();

  const [show, setShow] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleClose = useCallback(() => setShow(false), []);

  const { mutate, isLoading } = useMutation(() => deleteOption(selection.id, optionId), {
    onSuccess: updatedFullSelection => {
      setIsDeleteModalOpen(false);
      updateSelectionData({ actionType: 'updateSelectionContainer', payload: updatedFullSelection });
      toast.success(<FormattedMessage id="toast.success" />);
    },
    onError: (error: AxiosError) => {
      toast.error(getOptionDeletionErrorMessage({ error, offers, primaryLanguage }), {
        duration: Infinity,
      });
    },
  });

  return (
    <Root onClick={e => e.stopPropagation()}>
      <StyledPopover
        content={
          <PopoverContent>
            <MenuItemGroup>
              <MenuActionItem onClick={() => onCopy?.(optionId)} variant="black">
                <FormattedMessage id="menu-option.create-copy" />
              </MenuActionItem>

              <MenuActionItem onClick={() => setIsDeleteModalOpen(true)} variant="red">
                <FormattedMessage id="menu-option.delete" />
              </MenuActionItem>
            </MenuItemGroup>
          </PopoverContent>
        }
        onClickOutside={handleClose}
        onClickInside={handleClose}
        show={show}
      >
        <ToggleButton
          onClick={e => {
            e.preventDefault();
            setShow(!show);
          }}
        >
          <MoreHorizontal />
        </ToggleButton>
      </StyledPopover>
      {isDeleteModalOpen && (
        <ConfirmModal
          header={<FormattedMessage id="menu-option.delete" />}
          loading={isLoading}
          onCancel={() => setIsDeleteModalOpen(false)}
          onConfirm={() => mutate()}
          open
        />
      )}
    </Root>
  );
};
